import { useCallback } from 'react';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import {
  buildApplicationGroupPath,
  buildApplicationGroupDashboardPath,
  buildApplicationGroupCreatePath,
  buildApplicationGroupsPath,
  buildApplicationDashboardPath,
  buildApplicationPath,
  buildApiKeysPath,
  buildApplicationScopedPath,
  buildApplicationsPath,
  buildGroupScopedPath,
  buildUserPath,
  buildUsersPath,
  getApplicationSectionFromPath,
  getGroupSectionFromPath,
} from 'src/router/pathUtils';
import pages from 'src/router/routes';
import { ALL_SELECTION_ID, isAllSelection } from 'src/utils/selection';
import { useFeatureFlag } from 'switch-fe-shared/hooks/useFeatureFlag';

import useEffectiveApplicationGroupId from './useEffectiveApplicationGroupId';
import useEffectiveApplicationId from './useEffectiveApplicationId';

const isConcreteGroupId = (groupId?: string): groupId is string =>
  groupId !== undefined && !isAllSelection(groupId);

export const useAdminUrlNavigation = () => {
  const navigate = useNavigate();
  const effectiveGroupId = useEffectiveApplicationGroupId();
  const effectiveAppId = useEffectiveApplicationId();
  const isUrlNavigationEnabled = useFeatureFlag('ac_url_navigation__release');
  const neutralDashboardPath = isUrlNavigationEnabled
    ? pages.dashboard.path
    : pages.home.path;

  const getApplicationGroupPath = useCallback(
    (groupId: string) =>
      isUrlNavigationEnabled
        ? buildApplicationGroupPath(groupId)
        : pages.applicationGroup.view.path.replace(':id', groupId),
    [isUrlNavigationEnabled]
  );

  const getApplicationGroupsPath = useCallback(
    () =>
      isUrlNavigationEnabled
        ? buildApplicationGroupsPath()
        : pages.applicationGroup.path,
    [isUrlNavigationEnabled]
  );

  const getApplicationGroupCreatePath = useCallback(
    () =>
      isUrlNavigationEnabled
        ? buildApplicationGroupCreatePath()
        : pages.applicationGroup.create.path,
    [isUrlNavigationEnabled]
  );

  const getApplicationGroupDashboardPath = useCallback(
    (groupId = effectiveGroupId) =>
      isUrlNavigationEnabled && isConcreteGroupId(groupId)
        ? buildApplicationGroupDashboardPath(groupId)
        : neutralDashboardPath,
    [effectiveGroupId, isUrlNavigationEnabled, neutralDashboardPath]
  );

  const getApplicationPath = useCallback(
    (appId: string, groupId = effectiveGroupId) =>
      isUrlNavigationEnabled && isConcreteGroupId(groupId)
        ? buildApplicationPath(groupId, appId)
        : pages.application.view.path.replace(':id', appId),
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getApplicationDashboardPath = useCallback(
    (appId: string, groupId = effectiveGroupId) =>
      isUrlNavigationEnabled && isConcreteGroupId(groupId)
        ? buildApplicationDashboardPath(groupId, appId)
        : neutralDashboardPath,
    [effectiveGroupId, isUrlNavigationEnabled, neutralDashboardPath]
  );

  const getApplicationsPath = useCallback(
    (groupId = effectiveGroupId) =>
      isUrlNavigationEnabled && isConcreteGroupId(groupId)
        ? buildApplicationsPath(groupId)
        : pages.application.path,
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getUserPath = useCallback(
    (userId: string, groupId = effectiveGroupId) =>
      isUrlNavigationEnabled
        ? buildUserPath(groupId ?? ALL_SELECTION_ID, userId)
        : pages.users.view.path.replace(':id', userId),
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getUsersPath = useCallback(
    (groupId = effectiveGroupId) =>
      isUrlNavigationEnabled
        ? buildUsersPath(groupId ?? ALL_SELECTION_ID)
        : pages.users.path,
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getApiKeysPath = useCallback(
    (groupId = effectiveGroupId) =>
      isUrlNavigationEnabled
        ? buildApiKeysPath(groupId ?? ALL_SELECTION_ID)
        : pages.apiKeys.path,
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getGroupScopedPath = useCallback(
    (sectionPath: string, legacyPath: string, groupId = effectiveGroupId) =>
      isUrlNavigationEnabled && isConcreteGroupId(groupId)
        ? buildGroupScopedPath(groupId, sectionPath)
        : legacyPath,
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getGroupScopedChildPath = useCallback(
    (
      sectionPath: string,
      childPath: string,
      legacyPath: string,
      groupId = effectiveGroupId
    ) =>
      isUrlNavigationEnabled && isConcreteGroupId(groupId)
        ? buildGroupScopedPath(groupId, sectionPath, childPath)
        : legacyPath,
    [effectiveGroupId, isUrlNavigationEnabled]
  );

  const getApplicationScopedPath = useCallback(
    (
      sectionPath: string,
      legacyPath: string,
      groupId = effectiveGroupId,
      appId = effectiveAppId
    ) =>
      isUrlNavigationEnabled &&
      isConcreteGroupId(groupId) &&
      appId !== undefined &&
      !isAllSelection(appId)
        ? buildApplicationScopedPath(groupId, appId, sectionPath)
        : legacyPath,
    [effectiveAppId, effectiveGroupId, isUrlNavigationEnabled]
  );

  const getApplicationScopedChildPath = useCallback(
    (
      sectionPath: string,
      childPath: string,
      legacyPath: string,
      groupId = effectiveGroupId,
      appId = effectiveAppId
    ) =>
      isUrlNavigationEnabled &&
      isConcreteGroupId(groupId) &&
      appId !== undefined &&
      !isAllSelection(appId)
        ? buildApplicationScopedPath(groupId, appId, sectionPath, childPath)
        : legacyPath,
    [effectiveAppId, effectiveGroupId, isUrlNavigationEnabled]
  );

  const getGroupChangePath = useCallback(
    (groupId: string, pathname: string) => {
      if (isAllSelection(groupId)) {
        return neutralDashboardPath;
      }
      const section = isUrlNavigationEnabled
        ? getGroupSectionFromPath(pathname)
        : undefined;
      return section
        ? buildGroupScopedPath(groupId, section)
        : getApplicationGroupDashboardPath(groupId);
    },
    [
      isUrlNavigationEnabled,
      neutralDashboardPath,
      getApplicationGroupDashboardPath,
    ]
  );

  const getApplicationChangePath = useCallback(
    (appId: string, groupId: string | undefined, pathname: string) => {
      if (isAllSelection(appId)) {
        return getApplicationGroupDashboardPath(groupId);
      }
      const section = isUrlNavigationEnabled
        ? getApplicationSectionFromPath(pathname)
        : undefined;
      return section && isConcreteGroupId(groupId)
        ? buildApplicationScopedPath(groupId, appId, section)
        : getApplicationDashboardPath(appId, groupId);
    },
    [
      isUrlNavigationEnabled,
      getApplicationGroupDashboardPath,
      getApplicationDashboardPath,
    ]
  );

  const navigateToApplicationGroup = useCallback(
    (groupId: string, options?: NavigateOptions) =>
      navigate(getApplicationGroupPath(groupId), options),
    [getApplicationGroupPath, navigate]
  );

  const navigateToApplicationGroups = useCallback(
    (options?: NavigateOptions) =>
      navigate(getApplicationGroupsPath(), options),
    [getApplicationGroupsPath, navigate]
  );

  const navigateToApplicationGroupCreate = useCallback(
    (options?: NavigateOptions) =>
      navigate(getApplicationGroupCreatePath(), options),
    [getApplicationGroupCreatePath, navigate]
  );

  const navigateToApplicationGroupDashboard = useCallback(
    (groupId?: string, options?: NavigateOptions) =>
      navigate(getApplicationGroupDashboardPath(groupId), options),
    [getApplicationGroupDashboardPath, navigate]
  );

  const navigateToNeutralDashboard = useCallback(
    (options?: NavigateOptions) => navigate(neutralDashboardPath, options),
    [navigate, neutralDashboardPath]
  );

  const navigateToApplication = useCallback(
    (appId: string, groupId?: string, options?: NavigateOptions) =>
      navigate(getApplicationPath(appId, groupId), options),
    [getApplicationPath, navigate]
  );

  const navigateToApplicationDashboard = useCallback(
    (appId: string, groupId?: string, options?: NavigateOptions) =>
      navigate(getApplicationDashboardPath(appId, groupId), options),
    [getApplicationDashboardPath, navigate]
  );

  const navigateToApplications = useCallback(
    (groupId?: string, options?: NavigateOptions) =>
      navigate(getApplicationsPath(groupId), options),
    [getApplicationsPath, navigate]
  );

  const navigateToUser = useCallback(
    (userId: string, groupId?: string, options?: NavigateOptions) =>
      navigate(getUserPath(userId, groupId), options),
    [getUserPath, navigate]
  );

  const navigateToUsers = useCallback(
    (groupId?: string, options?: NavigateOptions) =>
      navigate(getUsersPath(groupId), options),
    [getUsersPath, navigate]
  );

  const navigateToGroupScopedPath = useCallback(
    (
      sectionPath: string,
      legacyPath: string,
      groupId?: string,
      options?: NavigateOptions
    ) =>
      navigate(getGroupScopedPath(sectionPath, legacyPath, groupId), options),
    [getGroupScopedPath, navigate]
  );

  const navigateToApplicationScopedPath = useCallback(
    (
      sectionPath: string,
      legacyPath: string,
      groupId?: string,
      appId?: string,
      options?: NavigateOptions
    ) =>
      navigate(
        getApplicationScopedPath(sectionPath, legacyPath, groupId, appId),
        options
      ),
    [getApplicationScopedPath, navigate]
  );

  return {
    isUrlNavigationEnabled,
    neutralDashboardPath,
    getApplicationGroupPath,
    getApplicationGroupsPath,
    getApplicationGroupCreatePath,
    getApplicationGroupDashboardPath,
    getApplicationPath,
    getApplicationDashboardPath,
    getApplicationsPath,
    getUserPath,
    getUsersPath,
    getApiKeysPath,
    getGroupScopedPath,
    getGroupScopedChildPath,
    getApplicationScopedPath,
    getApplicationScopedChildPath,
    getGroupChangePath,
    getApplicationChangePath,
    navigateToApplicationGroup,
    navigateToApplicationGroups,
    navigateToApplicationGroupCreate,
    navigateToApplicationGroupDashboard,
    navigateToNeutralDashboard,
    navigateToApplication,
    navigateToApplicationDashboard,
    navigateToApplications,
    navigateToUser,
    navigateToUsers,
    navigateToGroupScopedPath,
    navigateToApplicationScopedPath,
  };
};
